import React, { useEffect, useState } from "react";
import { Car, Plus, Download, X, Building2, User } from "lucide-react";
import { dataApi, formatApiError } from "../api/client";
import { EmptyState } from "./Dashboard";

const VEHICLE_TYPE_LABEL = {
  sedan: "Sedán", hatchback: "Hatchback", suv: "SUV",
  station_wagon: "Station Wagon", pickup: "Pick-up", van: "Van",
};

export default function FleetView() {
  const [vehicles, setVehicles] = useState(null);
  const [drivers, setDrivers] = useState([]);
  const [showCreate, setShowCreate] = useState(false);
  const [err, setErr] = useState("");

  const load = () => {
    dataApi.vehicles().then(setVehicles).catch((e) => setErr(formatApiError(e)));
    dataApi.drivers().then(setDrivers).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  return (
    <div className="space-y-4 fade-in" data-testid="fleet-view">
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <div className="text-xs font-semibold uppercase tracking-wider text-[#3949AB]">Operaciones</div>
          <h1 className="font-display font-bold text-2xl text-neutral-900 mt-0.5">Flota vehicular</h1>
          <p className="text-sm text-neutral-500 mt-1">
            Gestiona unidades propias de la empresa y vehículos de socios.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button className="btn-secondary"><Download className="w-3.5 h-3.5" strokeWidth={2.5}/>Exportar</button>
          <button className="btn-primary" data-testid="fleet-btn-new" onClick={() => setShowCreate(true)}>
            <Plus className="w-3.5 h-3.5" strokeWidth={2.5}/>Nueva unidad propia
          </button>
        </div>
      </div>

      {err && <div className="chip chip-danger">{err}</div>}

      <div className="office-card">
        {vehicles === null ? (
          <div className="p-8 text-center text-sm text-neutral-500">Cargando...</div>
        ) : vehicles.length === 0 ? (
          <EmptyState
            icon={Car}
            title="No hay vehículos registrados"
            desc='Crea una unidad propia desde el botón arriba, o registra un socio en Usuarios (el vehículo se creará automáticamente).'
          />
        ) : (
          <div className="p-4 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {vehicles.map((v) => (
              <VehicleCard key={v.id} v={v} />
            ))}
          </div>
        )}
      </div>

      {showCreate && (
        <CreateVehicleModal
          drivers={drivers}
          onClose={() => setShowCreate(false)}
          onCreated={() => { setShowCreate(false); load(); }}
        />
      )}
    </div>
  );
}

const VehicleCard = ({ v }) => {
  const isSocio = v.ownership === "socio";
  return (
    <div className={`p-4 border rounded ${isSocio ? "border-[#3949AB]/40 bg-[#EEF0FC]/40" : "border-neutral-200 bg-white"}`}>
      <div className="flex items-start justify-between mb-2">
        <div className="font-mono-grotesk font-bold text-lg text-neutral-900">{v.plate}</div>
        <span className={`chip ${isSocio ? "chip-indigo" : "chip-success"}`}>
          {isSocio ? <><User className="w-3 h-3" strokeWidth={2.5}/>Socio</> : <><Building2 className="w-3 h-3" strokeWidth={2.5}/>Propia</>}
        </span>
      </div>
      <div className="text-sm font-medium text-neutral-800">{v.model}</div>
      <div className="text-xs text-neutral-500">{v.year} · {VEHICLE_TYPE_LABEL[v.vehicle_type] || v.vehicle_type}</div>
      {v.driver_name && (
        <div className="mt-2 pt-2 border-t border-neutral-200 text-xs">
          <span className="text-neutral-500">Conductor:</span> <span className="font-medium">{v.driver_name}</span>
        </div>
      )}
    </div>
  );
};

const CreateVehicleModal = ({ drivers, onClose, onCreated }) => {
  const [form, setForm] = useState({ plate: "", brand: "", model: "", color: "",
                                      passenger_capacity: 4, vehicle_type: "sedan",
                                      ownership: "propia", driver_id: "" });
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    if (!form.plate || !form.model) { setErr("Placa y modelo son obligatorios"); return; }
    setSaving(true);
    try {
      await dataApi.createVehicle({
        plate: form.plate.toUpperCase(),
        brand: form.brand || null,
        model: form.model,
        color: form.color || null,
        passenger_capacity: parseInt(form.passenger_capacity, 10) || 4,
        vehicle_type: form.vehicle_type,
        ownership: form.ownership,
        driver_id: form.driver_id || null,
      });
      onCreated();
    } catch (e2) { setErr(formatApiError(e2)); }
    finally { setSaving(false); }
  };

  return (
    <>
      <div className="fixed inset-0 bg-black/30 z-40" onClick={onClose}></div>
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
        <form onSubmit={submit} className="bg-white rounded-lg shadow-2xl w-full max-w-lg fade-in" data-testid="create-vehicle-modal">
          <div className="px-5 py-3 border-b border-neutral-200 flex items-center justify-between">
            <h3 className="font-display font-semibold text-lg">Nueva unidad de flota</h3>
            <button type="button" onClick={onClose} className="btn-ghost !p-1.5"><X className="w-4 h-4" strokeWidth={2.5}/></button>
          </div>
          <div className="p-5 space-y-4">
            <div>
              <label className="block text-xs font-semibold uppercase tracking-wider text-neutral-500 mb-1.5">Tipo de propiedad *</label>
              <select value={form.ownership} onChange={(e) => setForm({...form, ownership: e.target.value})} className="office-input">
                <option value="propia">Unidad propia de la empresa</option>
                <option value="socio">Unidad de socio</option>
              </select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <FieldSm label="Placa *" value={form.plate} onChange={(v) => setForm({...form, plate: v.toUpperCase()})} placeholder="B7C-451" />
              <FieldSm label="Marca" value={form.brand} onChange={(v) => setForm({...form, brand: v})} placeholder="Toyota" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <FieldSm label="Modelo *" value={form.model} onChange={(v) => setForm({...form, model: v})} placeholder="Yaris" />
              <FieldSm label="Color" value={form.color} onChange={(v) => setForm({...form, color: v})} placeholder="Rojo" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <FieldSm label="Pasajeros *" value={form.passenger_capacity} onChange={(v) => setForm({...form, passenger_capacity: v})} placeholder="4" type="number" />
              <div>
                <label className="block text-xs font-semibold uppercase tracking-wider text-neutral-500 mb-1.5">Tipo de vehículo *</label>
                <select value={form.vehicle_type} onChange={(e) => setForm({...form, vehicle_type: e.target.value})} className="office-input">
                  {Object.entries(VEHICLE_TYPE_LABEL).map(([k, l]) => <option key={k} value={k}>{l}</option>)}
                </select>
              </div>
            </div>
            <div>
              <label className="block text-xs font-semibold uppercase tracking-wider text-neutral-500 mb-1.5">
                Conductor asignado {form.ownership === "socio" && "*"}
              </label>
              <select value={form.driver_id} onChange={(e) => setForm({...form, driver_id: e.target.value})} className="office-input">
                <option value="">— {form.ownership === "propia" ? "Sin asignar" : "Selecciona un conductor"} —</option>
                {drivers.map((d) => <option key={d.id} value={d.id}>{d.name} · {d.phone}</option>)}
              </select>
            </div>
            {err && <div className="chip chip-danger">{err}</div>}
          </div>
          <div className="px-5 py-3 border-t border-neutral-200 bg-neutral-50 flex justify-end gap-2 rounded-b-lg">
            <button type="button" onClick={onClose} className="btn-secondary">Cancelar</button>
            <button type="submit" disabled={saving} className="btn-primary" data-testid="new-vehicle-submit">
              {saving ? "Creando..." : "Crear unidad"}
            </button>
          </div>
        </form>
      </div>
    </>
  );
};

const FieldSm = ({ label, value, onChange, placeholder, type }) => (
  <div>
    <label className="block text-xs font-semibold uppercase tracking-wider text-neutral-500 mb-1.5">{label}</label>
    <input value={value} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} type={type || 'text'} className="office-input" />
  </div>
);
